import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { InfoModalComponent } from '@components/info-modal/info-modal.component';
import { ModalData } from '@modules/modal/interfaces';
import { ModalService } from '@modules/modal/services/modal.service';
import { catchError, filter, firstValueFrom, Observable, of, Subscription, interval, tap } from 'rxjs';

import { LoginUserDataI } from '@models/auth';
import { UserConfigResponse } from '@shared/models/auth/user-config-response.model';
import { appStateStore } from '@shared/stores/app-state.store';
import { DateFormat, MoneyFormat, UserConfigStore } from '@shared/stores/user-config.store';

@Injectable({
  providedIn: 'root',
})
export class AuthorizationService {
  private timerSubscription?: Subscription;
  private activitySubscription?: Subscription;
  private warningModal?: MatDialogRef<InfoModalComponent, any>;

  private readonly stateStore = inject(appStateStore);
  private readonly configStore = inject(UserConfigStore);

  readonly remainingTime = signal(+this.stateStore.appState.sessionTimeout() || 0);

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly modalService: ModalService,
  ) {
    this.initActivityTracking();
  }

  private normalizeMoneyFormat(value: string | null | undefined): MoneyFormat {
    switch (value) {
      case 'plain_dot':
      case 'space_comma':
      case 'dot_comma':
      case 'space_comma_suffix_eur':
        return value;
      default:
        return 'space_comma';
    }
  }

  private normalizeDateFormat(value: string | null | undefined): DateFormat {
    switch (value) {
      case 'dd.mm.yyyy':
      case 'yyyy-mm-dd':
      case 'dd_month_yyyy':
        return value;
      default:
        return 'dd.mm.yyyy';
    }
  }

  getVersion() {
    return this.http.post('/core/version', {});
  }

  getURL(data: any): Observable<any> {
    return this.http.post('/user/getURL', data);
  }

  logIn(data: any): Observable<LoginUserDataI> {
    return this.http.post<LoginUserDataI>('/user/login', data);
  }

  getUserConfig(): Observable<UserConfigResponse> {
    return this.http.post<UserConfigResponse>('/user/config', {}).pipe(
      catchError(() =>
        of({
          moneyFormat: 'space_comma',
          dateFormat: 'dd.mm.yyyy',
        }),
      ),
    );
  }

  getUserConfigByUrl(baseUrl: string): Observable<UserConfigResponse> {
    return this.http.post<UserConfigResponse>(`${baseUrl}/user/config`, {}).pipe(
      catchError(() =>
        of({
          moneyFormat: 'space_comma',
          dateFormat: 'dd.mm.yyyy',
        }),
      ),
    );
  }

  loadUserConfig(): Observable<UserConfigResponse> {
    return this.getUserConfig().pipe(
      tap((config) => {
        this.configStore.setConfig({
          moneyFormat: this.normalizeMoneyFormat(config.moneyFormat),
          dateFormat: this.normalizeDateFormat(config.dateFormat),
        });
      }),
    );
  }

  loadUserConfigByUrl(baseUrl: string): Observable<UserConfigResponse> {
    return this.getUserConfigByUrl(baseUrl).pipe(
      tap((config) => {
        this.configStore.setConfig({
          moneyFormat: this.normalizeMoneyFormat(config.moneyFormat),
          dateFormat: this.normalizeDateFormat(config.dateFormat),
        });
      }),
    );
  }

  updateRemainingTime(): void {
    const sessionTimeout = +this.stateStore.appState.sessionTimeout();

    if (!sessionTimeout) return;

    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }

    this.remainingTime.set(sessionTimeout);
    this.startSessionTimeoutWatcher();
  }

  private startSessionTimeoutWatcher(): void {
    this.timerSubscription = interval(60000).subscribe(() => {
      const currentTime = this.remainingTime();

      if (currentTime <= 1) {
        this.logoutByTimeout();
        return;
      }

      const nextTime = currentTime - 1;
      this.remainingTime.set(nextTime);

      if (nextTime === 3 && !this.warningModal) {
        this.warningModal = this.returnModal();

        this.warningModal.afterClosed().subscribe(() => {
          this.warningModal = undefined;
        });
      }
    });
  }

  private initActivityTracking(): void {
    this.activitySubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.resetTimerOnNavigation();
      });
  }

  private resetTimerOnNavigation(): void {
    const hasToken = !!this.stateStore.appState.token();
    const sessionTimeout = +this.stateStore.appState.sessionTimeout();

    if (!hasToken || !sessionTimeout) return;

    this.remainingTime.set(sessionTimeout);

    if (this.warningModal) {
      this.warningModal.close();
      this.warningModal = undefined;
    }
  }

  resetTimerOnBackendResponse(): void {
    const hasToken = !!this.stateStore.appState.token();
    const sessionTimeout = +this.stateStore.appState.sessionTimeout();

    if (!hasToken || !sessionTimeout) return;

    this.remainingTime.set(sessionTimeout);

    if (this.warningModal) {
      this.warningModal.close();
      this.warningModal = undefined;
    }
  }

  private logoutByTimeout(): void {
    this.stateStore.removeState();
    this.configStore.reset();
    this.router.navigate(['/']);

    if (this.warningModal) {
      this.warningModal.close();
      this.warningModal = undefined;
    }

    this.showModalInfo();

    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = undefined;
    }

    if (this.activitySubscription) {
      this.activitySubscription.unsubscribe();
      this.activitySubscription = undefined;
    }
  }

  async checkForAuthentication() {
    if (this.stateStore.appState.token()) {
      try {
        const response = await firstValueFrom(this.http.post<any>('/user/session', {}, { observe: 'response' }));

        const isAuthenticated = response.body?.result?.resultCode === 200;

        if (isAuthenticated) {
          this.updateRemainingTime();

          if (!this.configStore.loaded()) {
            await firstValueFrom(this.loadUserConfig());
          }
        }

        return isAuthenticated;
      } catch {
        return false;
      }
    }

    return false;
  }

  returnModal() {
    return this.modalService.infoModal(new ModalData('modal.session', 'modal.sessionTime', false, false), true);
  }

  showModalInfo() {
    this.modalService.infoModal(new ModalData('modal.whoops', 'modal.sessionTimeOf', false));
  }
}
